import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import httpClient, { extractErrorMessage } from '../Services/httpClient';

const PAGE_SIZE = 20;

const FEATURES = [
    'Categorias e Serviços',
    'Preço da Lemon Coin',
    'Precificação de Funcionalidades',
    'Transações'
];

const OPERATION_COLORS = {
    Added: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
    Modified: { backgroundColor: '#fff4ec', color: '#f77513' },
    Deleted: { backgroundColor: '#fdecea', color: '#c62828' }
};

const ActivityLogPanel = () => {
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [feature, setFeature] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await httpClient.get('/backoffice/activity-log', {
                params: { page, pageSize: PAGE_SIZE, feature: feature || undefined }
            });
            setItems(response.data.items);
            setTotalCount(response.data.totalCount);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }, [page, feature]);

    useEffect(() => {
        load();
    }, [load]);

    const handleFeatureChange = (event) => {
        setPage(1);
        setFeature(event.target.value);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold', mb: 2 }}>
                Activity Log
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField select label="Funcionalidade" size="small" value={feature} onChange={handleFeatureChange} sx={{ minWidth: 260 }}>
                    <MenuItem value="">Todas</MenuItem>
                    {FEATURES.map((f) => (
                        <MenuItem key={f} value={f}>{f}</MenuItem>
                    ))}
                </TextField>
            </Box>

            {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} style={{ color: '#f77513' }} />
                </Box>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                <TableCell>Data</TableCell>
                                <TableCell>Funcionalidade</TableCell>
                                <TableCell>Entidade</TableCell>
                                <TableCell>Operação</TableCell>
                                <TableCell>Campos alterados</TableCell>
                                <TableCell>Alterado por</TableCell>
                                <TableCell>Aprovado por</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" style={{ color: '#aaa', padding: '24px' }}>
                                        Nenhum registro de atividade encontrado
                                    </TableCell>
                                </TableRow>
                            )}
                            {items.map((item, index) => (
                                <TableRow key={`${item.entityId}-${item.timestamp}-${index}`}>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>{new Date(item.timestamp).toLocaleString('pt-BR')}</TableCell>
                                    <TableCell>{item.feature}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.entityName}</Typography>
                                        <Typography variant="caption" color="text.secondary" style={{ fontFamily: 'monospace' }}>
                                            {item.entityId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={item.operation} size="small" style={OPERATION_COLORS[item.operation] || {}} />
                                    </TableCell>
                                    <TableCell>
                                        {item.changedFields.length === 0
                                            ? <Typography variant="caption" color="text.secondary">—</Typography>
                                            : item.changedFields.map((f) => (
                                                <Box key={f.field} sx={{ fontSize: '12px', mb: 0.25 }}>
                                                    <strong>{f.field}</strong>: {f.oldValue ?? '—'} → {f.newValue ?? '—'}
                                                </Box>
                                            ))
                                        }
                                    </TableCell>
                                    <TableCell>{item.changedBy}</TableCell>
                                    <TableCell>
                                        {item.approvedBy ?? <Typography variant="caption" color="text.secondary">Não requerido</Typography>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Página {page} de {totalPages} ({totalCount} registros)
                </Typography>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default ActivityLogPanel;
